import React, { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { prefixLocale } from '@helpers/env'
import Translations from '@helpers/translations'
import { ReturnsHeaderImage } from '@components/returns/ReturnsHeaderImage'
import Notification from '@components/ui/Notification'
import { getReturnStatus } from '@api/returns'
import ItemsSelection from './ItemsSelection'
import CarrierSelection from './CarrierSelection'
import ReturnsHeader from './ReturnsHeader'
import OrderForm from './OrderForm'
import styles from './styles.module.scss'
import formData from './formData'
import Footer from '@components/Footer'

const ReturnPageContent = () => {
  const router = useRouter()
  const {
    order_number: orderNumber,
    postcode,
    return_id: returnId,
    error,
    successReturn
  } = router.query as {
    order_number: string | undefined
    postcode: string | undefined
    return_id: string | undefined
    error: string | undefined
    successReturn: string | undefined
  }

  const [label, setLabel] = useState({ encodedLabel: '', encoding: '' })
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const onSubmit = (queryParams: Record<string, string>) => {
    const queryStringParameters = Object.keys(queryParams)
      .map(key => `${key}=${queryParams[key]}`)
      .join('&')
    const href = `/returns?${queryStringParameters}`
    const as = `${prefixLocale()}/pages${href}`
    router.push(href, as, { shallow: true })
  }

  if (process.env.RETURNS_MAINTENANCE_MODE === 'true') {
    return (
      <>
        <ReturnsHeaderImage content={formData.header} />
        <Notification
          type="warning"
          content={
            <>
              <p>
                {Translations.localise(
                  'pages.returns.returns-notifications.maintenance-mode.copy-1'
                )}
              </p>
              <p>
                {Translations.localise(
                  'pages.returns.returns-notifications.maintenance-mode.copy-2'
                )}
              </p>
            </>
          }
        />
      </>
    )
  }

  if (returnId && orderNumber && postcode) {
    return (
      <CarrierSelection
        headerImage={formData.header.image}
        returnId={returnId}
        orderNumber={orderNumber}
        postcode={postcode}
        setLabel={setLabel}
      />
    )
  }

  if (orderNumber && postcode) {
    return (
      <ItemsSelection
        error={error}
        successReturn={successReturn}
        orderNumber={orderNumber}
        postcode={postcode}
        onSubmit={onSubmit}
        label={label}
      />
    )
  }

  return isClient && <OrderForm formData={formData} onSubmit={onSubmit} />
}

const ReturnPage = () => {
  const [isServiceUnavailable, setIsServiceUnavailable] = useState(false)

  useEffect(() => {
    getReturnStatus()
      .then(response => {
        setIsServiceUnavailable(response.status !== 200)
      })
      .catch(() => {
        setIsServiceUnavailable(true)
      })
  }, [])

  return (
    <div className={styles.returns__wrapper}>
      <ReturnsHeader />
      <Notification
        type="error"
        theme="dark"
        content={
          <p>
            Our Returns Portal is having a temporary issue generating shipping
            labels. We’re on it and expect things to be back to normal soon.{' '}
            <br />
            Please try again in a little while and thank you for your patience.
            <br />
            We apologise for any inconvenience.
          </p>
        }
      />
      {isServiceUnavailable && (
        <Notification
          type="error"
          theme="dark"
          content={
            <p>
              We are currently experiencing some issues with our returns
              provider. <br />
              If your order cannot be found, please try again in 24 hours. If
              it’s urgent, email us at hello@spoke-london.com.
              <br />
              We apologise for any inconvenience.
            </p>
          }
        />
      )}
      <div className={styles.returns__contentWrapper}>
        <ReturnPageContent />
      </div>
      <Footer />
    </div>
  )
}

export default ReturnPage
